import { useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Translate, useTranslation } from "@privacy/translations";
import { useMutation, useQueries } from "@tanstack/react-query";
import * as FormDialog from "../shared/FormDialog";
import { shareSharedProcessingMutationOptions } from "../api/sharedProcessings";
import { responsibleCandidatesForTenantsQueryOptions } from "../api/registrations";
import { hasErrorCode } from "../api/errors";
import { TenantIcon3 } from "../shared/icons";
import { UserSelect } from "../shared/UserSelect";
import { pickResponsibleId } from "../shared/ResponsibleField";
import { STATUS_WORDS } from "../shared/StatusWord";

export const ShareSharedProcessingDialog = ({
  sharedProcessingId,
  organisations,
  onClose,
}: {
  sharedProcessingId: string;
  organisations: Array<{ organisationId: string; organisationName: string }>;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const shareMutation = useMutation(shareSharedProcessingMutationOptions());
  const pluralSuffix = organisations.length > 1 ? "Plural" : "Singular";
  const candidateQueries = useQueries({
    queries: organisations.map(organisation =>
      responsibleCandidatesForTenantsQueryOptions([organisation.organisationId])
    ),
  });
  const [pickedResponsibleIds, setPickedResponsibleIds] = useState<Record<string, string | null>>({});

  const rows = organisations.map((organisation, index) => {
    const candidateQuery = candidateQueries[index];
    const candidates = candidateQuery?.isPending ? undefined : (candidateQuery?.data?.users ?? []);
    const responsibleId = pickResponsibleId(
      pickedResponsibleIds[organisation.organisationId],
      candidates,
      candidateQuery?.data?.defaultResponsibleId
    );

    return { organisation, candidates, responsibleId };
  });
  const responsibleIds = Object.fromEntries(
    rows.flatMap(row => (row.responsibleId ? [[row.organisation.organisationId, row.responsibleId]] : []))
  );

  return (
    <FormDialog.Dialog isOpen maxWidth="720px">
      <FormDialog.Form
        onSubmit={event => {
          event.preventDefault();
          shareMutation.mutate(
            {
              id: sharedProcessingId,
              organisationIds: organisations.map(organisation => organisation.organisationId),
              responsibleIds,
            },
            { onSuccess: onClose }
          );
        }}
      >
        <FormDialog.Title>
          {t(`SharedProcessing.Share.Dialog.Title${pluralSuffix}`, { numberOfOrganisations: organisations.length })}
        </FormDialog.Title>
        <FormDialog.Content>
          <FormDialog.FormError
            isError={shareMutation.isError}
            errorMessage={
              hasErrorCode(shareMutation.error, "DESCRIPTION_NOT_APPROVED") ? (
                <Translate translationKey="SharedProcessing.PresentIn.NotApproved" values={STATUS_WORDS} />
              ) : undefined
            }
          />

          <Typography
            sx={{
              pb: 3,
              fontSize: theme => theme.typography.pxToRem(14),
              color: theme => theme.palette.text.secondary,
            }}
          >
            {t(`SharedProcessing.Share.Dialog.Body${pluralSuffix}`)}
          </Typography>
          <Stack component="ul" sx={{ m: 0, p: 0, listStyle: "none", maxHeight: 320, overflowY: "auto" }}>
            {rows.map(({ organisation, candidates, responsibleId }) => (
              <Stack
                component="li"
                direction="row"
                key={organisation.organisationId}
                sx={theme => ({
                  "alignItems": "center",
                  "flexWrap": "wrap",
                  "gap": 1.5,
                  "py": 1.5,
                  "&:not(:last-of-type)": { borderBottom: `1px solid ${theme.palette.divider}` },
                })}
              >
                <Stack direction="row" sx={{ flex: 1, minWidth: 0, alignItems: "center", gap: 1.5 }}>
                  <TenantIcon3 size={16} color={theme => theme.palette.text.primary} />
                  <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), overflowWrap: "anywhere" }}>
                    {organisation.organisationName}
                  </Typography>
                </Stack>
                <Box sx={{ width: { xs: "100%", sm: 380 }, flex: "none" }}>
                  <UserSelect
                    id={`shareResponsible-${organisation.organisationId}`}
                    selectedId={responsibleId}
                    users={candidates}
                    isClearable
                    placeholder={t("SharedProcessing.Share.Dialog.ResponsiblePlaceholder")}
                    noOptionsText={
                      candidates?.length === 0
                        ? t("SharedProcessing.Share.Dialog.NoCandidates", {
                            organisation: organisation.organisationName,
                          })
                        : undefined
                    }
                    onSelectUser={userId =>
                      setPickedResponsibleIds(previous => ({
                        ...previous,
                        [organisation.organisationId]: userId || null,
                      }))
                    }
                  />
                </Box>
              </Stack>
            ))}
          </Stack>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton
            isSubmitting={shareMutation.isPending}
            isDisabled={rows.some(row => !row.candidates)}
          >
            {t("SharedProcessing.Share.Dialog.ConfirmButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
